import { ReactElement, useContext, useEffect, useState } from 'react';
import Modal from '@components/Modal';
import Button from '@components/DesignLibrary/Button';
import Image from 'next/image';
import PACERLogo from '@lib/Creators/Dashboard/Integrations/assets/pacer.png';
import DocumentRow from '@components/UserWorkflowInfo/DocumentsAction/DocumentRow';
import { Input } from '@/components/DesignLibrary/Atoms/Input';
import removeNullish from '@helpers/removeNullish';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import Spacer from '@components/Spacer';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import TeamsContext from '@providers/Teams/TeamsContext';
import { DISTRICT_COURT_DATA, getDistrictIdByName, getDistrictNameById } from '@/helpers/districtCourtData';
import Select from '@/components/DesignLibrary/Atoms/Select/Select';
import s from './ECFManagerModal.module.scss';

type Props = {
  onClose: () => void;
  documents: Array<Parameters<typeof DocumentRow>[0]['compiledDocument']>;
  userWorkflowId: string;
  onSubmit?: () => Promise<void>;
};
const BANKRUPTCY_SCHEDULES_PROVIDER_LOCATER = 'noodle-bankruptcy-schedules-questionnaire-refactor';
const fieldTemplateKey = 'forge1b3f3bf097d211efb626932a321272a6';

const ECFManagerModal = ({ onClose, documents, userWorkflowId, onSubmit }: Props): ReactElement => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [district, setDistrict] = useState('');
  const [step, setStep] = useState(1);
  const {
    fetchingState: { isFetching: isSubmittingCase },
    getData: submitCase,
  } = useNoodleApi(tsClient.integrations.submitCase, { toastOnError: true });
  const { creatorId } = useContext(TeamsContext);
  let questionnaireId = '';
  const {
    getData: getQuestionnaireResponsesByTemplateKey,
  } = useNoodleApi(tsClient.questionnaires.getQuestionnaireResponsesByTemplateKey);
  const {
    getData: getQuestionnaireIdByUserWorkflow,
  } = useNoodleApi(tsClient.workflows.getQuestionnaireIdByUserWorkflow);

  useEffect(() => {
    const fetchResponses = async (): Promise<void> => {
      const response = await getQuestionnaireIdByUserWorkflow({
        questionnaireTemplateSlug: BANKRUPTCY_SCHEDULES_PROVIDER_LOCATER,
        userWorkflowId,
      });
      questionnaireId = response?.data?.questionnaireId ?? '';
      const { data } = await getQuestionnaireResponsesByTemplateKey({
        fieldTemplateKey,
        questionnaireId,
      });
      const courtKey = typeof data?.value === 'string' ? getDistrictIdByName(data.value) : null;
      if (courtKey) {
        setDistrict(courtKey);
      }
    };
    fetchResponses();
  }, [getQuestionnaireResponsesByTemplateKey]);

  const handleDistrictSelection = (id: string): void => {
    setDistrict(id);
  };
  const onClick = async (): Promise<void> => {
    if (creatorId) {
      await submitCase({
        creatorId,
        district: getDistrictNameById(district),
        documentIds: documents.map((d) => d.document?.id).filter(removeNullish),
        password,
        provider: 'pacer',
        userWorkflowId,
        username,
      });
      if (onSubmit) {
        await onSubmit();
      }
      onClose();
    }
  };
  return (
    <Modal
      title={
        step === 1
          ? (
            <div className={s.modalTitle}>
              <Image src={PACERLogo} alt="Download" width={16} height={16} /> ECF Manager
            </div>
          )
          : ''
      }
      onClose={step > 1 ? () => setStep(step - 1) : onClose}
      closeIcon={step > 1 ? <ArrowCircleLeft size={24} weight="fill" /> : undefined}
    >
      <div className={s.container}>
        {step === 1 && (
          <>
            <div className={s.header}>
              <h2 className="heading-sm">Forms ready for filing</h2>
              <p className="caption">Review before syncing the case data</p>
            </div>
            <ul className={s.list}>
              {documents.map(doc => (
                <li key={doc.id}>
                  <DocumentRow compiledDocument={doc} fileNameOnly />
                </li>
              ))}
            </ul>
            <Button size="lg" variant="primary" onClick={() => setStep(2)}>
              Proceed
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <div className={s.header}>
              <Image src={PACERLogo} alt="Download" width={120} height={120} />
              <Spacer />
              <h2 className="heading-sm">Sync Data to PACER</h2>
            </div>
            <Input
              label="Username"
              id="username"
              value={username}
              onChange={setUsername}
              compact
              optional
            />
            <Input
              label="Password"
              id="password"
              value={password}
              onChange={setPassword}
              type='password'
              compact
              optional
            />
            <Select
              label="Filing District"
              compact
              options={DISTRICT_COURT_DATA}
              value={district}
              optional={true}
              onChange={handleDistrictSelection}
            />
            <Button
              size="lg"
              variant="ai"
              onClick={onClick}
              disabled={!username || !password || !district}
              loading={isSubmittingCase}
            >
              Sync Case Data to PACER
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ECFManagerModal;
